import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { 
  TrendingUp, TrendingDown, DollarSign, RefreshCw, 
  Cloud, AlertTriangle, Loader2, Link as LinkIcon,
  Eye, EyeOff
} from 'lucide-react';
import { useDemoData } from './useDemoData';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import { useAWSCredentials } from './AWSConnect';
import { useAzureCredentials } from './AzureConnect';
import { useGCPCredentials } from './GCPConnect';
import AWSConnect from './AWSConnect';
import AzureConnect from './AzureConnect';
import GCPConnect from './GCPConnect';
import SnowflakeConnect, { useSnowflakeCredentials } from './SnowflakeConnect';
import DatabricksConnect, { useDatabricksCredentials } from './DatabricksConnect';
import { useAuth } from '../context/AuthContext';
import { useSelectedProviders } from '../hooks/useSelectedProviders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_COLORS = ['#059669', '#0EA5E9', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];

const CloudCostDashboard = ({ provider = 'all', onDataLoaded }) => {
  const { credentials: awsCreds, isConnected: awsConnected } = useAWSCredentials();
  const { credentials: azureCreds, isConnected: azureConnected } = useAzureCredentials();
  const { credentials: gcpCreds, isConnected: gcpConnected } = useGCPCredentials();
  const { isConnected: snowflakeConnected, updateCredentials: setSnowflakeCreds } = useSnowflakeCredentials();
  const { isConnected: databricksConnected, updateCredentials: setDatabricksCreds } = useDatabricksCredentials();
  const { isAdmin, token } = useAuth();
  const { providerDetails, shouldShowAll } = useSelectedProviders();

  // Map provider id → live connection status where we actually support it
  const liveConnectionMap = {
    aws: awsConnected,
    azure: azureConnected,
    gcp: gcpConnected,
    snowflake: snowflakeConnected,
    databricks: databricksConnected,
  };

  // Which connect modal is currently open ('aws' | 'azure' | 'gcp' | 'snowflake' | 'databricks' | null)
  const [openConnector, setOpenConnector] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [awsData, setAwsData] = useState(null);
  const [azureData, setAzureData] = useState(null);
  const [gcpData, setGcpData] = useState(null);
  const [liveDataVisible, setLiveDataVisible] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const fetchAllData = async () => {
    setLoading(true);
    
    // Reset data
    setAwsData(null);
    setAzureData(null);
    setGcpData(null);

    const promises = [];

    // Fetch AWS data if connected
    if (awsConnected && awsCreds && (provider === 'all' || provider === 'aws')) {
      promises.push(
        Promise.all([
          axios.post(`${API_URL}/api/aws/cost-summary`, { credentials: awsCreds, days: 30 }),
          axios.post(`${API_URL}/api/aws/daily-trends`, { credentials: awsCreds, days: 30 })
        ]).then(([summary, trends]) => {
          setAwsData({
            provider: 'AWS',
            summary: summary.data,
            trends: trends.data
          });
        }).catch(err => {
          console.error('AWS fetch error:', err);
          setAwsData(null);
        })
      );
    }

    // Fetch Azure data if connected
    if (azureConnected && azureCreds && (provider === 'all' || provider === 'azure')) {
      promises.push(
        Promise.all([
          axios.post(`${API_URL}/api/azure/cost-summary`, { credentials: azureCreds, days: 30 }),
          axios.post(`${API_URL}/api/azure/daily-trends`, { credentials: azureCreds, days: 30 })
        ]).then(([summary, trends]) => {
          setAzureData({
            provider: 'Azure',
            summary: summary.data,
            trends: trends.data
          });
        }).catch(err => {
          console.error('Azure fetch error:', err);
          setAzureData(null);
        })
      );
    }

    // Fetch GCP data if connected
    if (gcpConnected && gcpCreds && (provider === 'all' || provider === 'gcp')) {
      promises.push(
        axios.post(`${API_URL}/api/gcp/cost-summary`, { credentials: gcpCreds, days: 30 })
          .then(response => {
            setGcpData({
              provider: 'GCP',
              summary: response.data,
              note: response.data.note
            });
          }).catch(err => {
            console.error('GCP fetch error:', err);
            setGcpData(null);
          })
      );
    }

    await Promise.allSettled(promises);
    setLoading(false);

    if (onDataLoaded) {
      onDataLoaded({
        aws: awsData,
        azure: azureData,
        gcp: gcpData
      });
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [awsConnected, azureConnected, gcpConnected]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Cloud data refreshed');
  };

  // Admin toggle to show/hide live data from connected providers
  const handleToggleLiveData = () => {
    if (!isAdmin) return;
    setLiveDataVisible(prev => !prev);
    toast.success(liveDataVisible ? 'Live data hidden' : 'Live data visible');
  };

  // Calculate combined totals
  const totalCost = (awsData?.summary?.total_cost || 0) + 
                    (azureData?.summary?.total_cost || 0) +
                    (gcpData?.summary?.estimated_cost || 0);
  
  const hasAnyConnection = awsConnected || azureConnected || gcpConnected;
  const hasData = awsData || azureData || gcpData;
  const { isDemoUser } = useDemoData();

  // Show connection prompt when no cloud accounts connected (skip for demo user)
  if (!hasAnyConnection && !isDemoUser) {
    return (
      <Card className="border-dashed border-2 border-slate-300 dark:border-slate-600" data-testid="cloud-cost-empty-state">
        <CardContent className="py-12 text-center">
          <Cloud className="w-16 h-16 mx-auto text-slate-400 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Connect Your Cloud Providers
          </h3>
          <p className="text-sm text-slate-400 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Connect your AWS, Azure, GCP, Snowflake, Databricks, or DigitalOcean accounts to see real cost insights, savings opportunities, and AI-powered recommendations.
          </p>
          <Link to="/cloud-audit">
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-cloud-btn">
              <LinkIcon className="w-4 h-4 mr-2" />
              Connect Cloud Account
            </Button>
          </Link>
          <div className="mt-8 grid grid-cols-4 gap-4 max-w-md mx-auto">
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-1">
                <span className="text-orange-600 dark:text-orange-400 font-bold text-xs">AWS</span>
              </div>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-1">
                <span className="text-blue-600 dark:text-blue-400 font-bold text-xs">Azure</span>
              </div>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-1">
                <span className="text-red-600 dark:text-red-400 font-bold text-xs">GCP</span>
              </div>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center mx-auto mb-1">
                <Cloud className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6" data-testid="cloud-cost-loading">
        {/* Animated Loading Header */}
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-9 w-24" />
        </div>

        {/* Total Cost Card Skeleton with Shimmer Effect */}
        <div className="relative overflow-hidden rounded-lg bg-slate-100 bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <div className="py-6 px-6">
            <Skeleton className="h-4 w-40 mb-3 bg-slate-300/50 dark:bg-slate-600/50" />
            <Skeleton className="h-10 w-48 bg-slate-300/50 dark:bg-slate-600/50" />
          </div>
        </div>

        {/* Provider Cards Grid Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {['aws', 'azure', 'gcp'].map((provider) => (
            <Card key={provider} className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-8 h-8 rounded" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                  <Skeleton className="h-5 w-12 rounded-full" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Cost Amount */}
                  <div>
                    <Skeleton className="h-8 w-28 mb-1" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  
                  {/* Chart Area Skeleton */}
                  <div className="h-32 relative overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 h-20 flex items-end justify-around px-2 pb-2">
                      {[40, 60, 35, 80, 55, 70, 45].map((h, i) => (
                        <div key={i} className="w-3 bg-slate-200 dark:bg-slate-700 rounded-t" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>

                  {/* Services List Skeleton */}
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((_, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Connection Status Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-36" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              {[1, 2, 3].map((_, idx) => (
                <div key={idx} className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <Skeleton className="h-4 w-12 mx-auto mb-2" />
                  <Skeleton className="h-5 w-20 mx-auto rounded-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Loading Indicator Text */}
        <div className="flex items-center justify-center gap-3 py-4">
          <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
          <span className="text-sm text-slate-400 dark:text-slate-400">
            Loading your cloud cost data...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cloud-cost-dashboard">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">
            Live Cloud Costs
          </h2>
          <p className="text-sm text-slate-400 dark:text-slate-400">
            Real-time cost data from connected providers
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && hasData && (
            <Button 
              variant={liveDataVisible ? "default" : "outline"}
              size="sm" 
              onClick={handleToggleLiveData}
              className={liveDataVisible 
                ? "bg-emerald-600 hover:bg-emerald-700 gap-2" 
                : "gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
              }
              data-testid="toggle-live-data-btn"
            >
              {liveDataVisible ? (
                <><Eye className="w-4 h-4" /> Data Visible</>
              ) : (
                <><EyeOff className="w-4 h-4" /> Data Hidden</>
              )}
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Combined Total */}
      <Card className="bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-500">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="text-white">
              <p className="text-sm opacity-90">Total Cloud Spend (30 days)</p>
              <p className="text-4xl font-bold">{formatCurrency(totalCost)}</p>
            </div>
            <div className="flex gap-2">
              {awsConnected && (
                <Badge className="bg-orange-500 text-white">AWS</Badge>
              )}
              {azureConnected && (
                <Badge className="bg-blue-500 text-white">Azure</Badge>
              )}
              {gcpConnected && (
                <Badge className="bg-red-500 text-white">GCP</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AWS Card */}
        {awsConnected && awsData && (
          <Card data-testid="aws-cost-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-orange-100 flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-xs">AWS</span>
                  </div>
                  Amazon Web Services
                </CardTitle>
                <Badge variant="secondary">Live</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-3xl font-bold text-orange-600">
                    {formatCurrency(awsData.summary?.total_cost || 0)}
                  </p>
                  <p className="text-sm text-slate-400">
                    {awsData.summary?.period_days || 30} day total
                  </p>
                </div>

                {/* Daily Trend Chart */}
                {awsData.trends?.daily_costs && awsData.trends.daily_costs.length > 0 && (
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                      <AreaChart data={awsData.trends.daily_costs}>
                        <defs>
                          <linearGradient id="awsGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <Area 
                          type="monotone" 
                          dataKey="cost" 
                          stroke="#F97316" 
                          fill="url(#awsGradient)"
                          strokeWidth={2}
                        />
                        <Tooltip 
                          formatter={(value) => [formatCurrency(value), 'Cost']}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Top Services */}
                {awsData.summary?.cost_by_service && awsData.summary.cost_by_service.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-400 mb-2">Top Services</p>
                    <div className="space-y-1">
                      {awsData.summary.cost_by_service.slice(0, 5).map((svc, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-slate-400 truncate max-w-[150px]" title={svc.service}>
                            {svc.service.replace('Amazon ', '').replace('AWS ', '')}
                          </span>
                          <span className="font-medium">{formatCurrency(svc.cost)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Azure Card */}
        {azureConnected && azureData && (
          <Card data-testid="azure-cost-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-xs">AZ</span>
                  </div>
                  Microsoft Azure
                </CardTitle>
                <Badge variant="secondary">Live</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatCurrency(azureData.summary?.total_cost || 0)}
                  </p>
                  <p className="text-sm text-slate-400">
                    {azureData.summary?.period_days || 30} day total
                  </p>
                </div>

                {/* Daily Trend Chart */}
                {azureData.trends?.daily_costs && azureData.trends.daily_costs.length > 0 && (
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                      <AreaChart data={azureData.trends.daily_costs}>
                        <defs>
                          <linearGradient id="azureGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <Area 
                          type="monotone" 
                          dataKey="cost" 
                          stroke="#3B82F6" 
                          fill="url(#azureGradient)"
                          strokeWidth={2}
                        />
                        <Tooltip 
                          formatter={(value) => [formatCurrency(value), 'Cost']}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Top Services */}
                {azureData.summary?.cost_by_service && azureData.summary.cost_by_service.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-400 mb-2">Top Services</p>
                    <div className="space-y-1">
                      {azureData.summary.cost_by_service.slice(0, 5).map((svc, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-slate-400 truncate max-w-[150px]" title={svc.service}>
                            {svc.service}
                          </span>
                          <span className="font-medium">{formatCurrency(svc.cost)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* GCP Card */}
        {gcpConnected && gcpData && (
          <Card data-testid="gcp-cost-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-red-100 flex items-center justify-center">
                    <span className="text-red-600 font-bold text-xs">GCP</span>
                  </div>
                  Google Cloud
                </CardTitle>
                <Badge variant="secondary">Connected</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {gcpData.note ? (
                  <div className="text-center py-4">
                    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      {gcpData.note}
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      Enable BigQuery billing export for detailed costs
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-sm text-slate-400">Billing Account</p>
                      <p className="font-medium">
                        {gcpData.summary?.billing_account?.displayName || 'Connected'}
                      </p>
                    </div>
                    {gcpData.summary?.available_services && (
                      <div>
                        <p className="text-sm font-medium text-slate-400 mb-2">
                          Active Services ({gcpData.summary.available_services.length})
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {gcpData.summary.available_services.slice(0, 5).map((svc, idx) => (
                            <div key={idx} className="text-sm text-slate-400">
                              {svc.displayName}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Connection Status Summary — dynamically shows only the user's clouds */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Connection Status</CardTitle>
          <CardDescription className="text-xs">
            {shouldShowAll ? 'All supported clouds' : `${providerDetails.length} cloud${providerDetails.length === 1 ? '' : 's'} configured`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {providerDetails.length === 0 ? (
            <div className="text-center py-6">
              <Cloud className="w-8 h-8 text-slate-300 dark:text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No clouds configured yet.</p>
              <Link to="/settings" className="text-xs text-emerald-600 hover:underline font-medium mt-1 inline-block">
                Choose your clouds →
              </Link>
            </div>
          ) : (
            <div className={`grid gap-3 ${
              providerDetails.length <= 3 ? 'grid-cols-3' :
              providerDetails.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' :
              'grid-cols-2 sm:grid-cols-3 md:grid-cols-5'
            }`}>
              {providerDetails.map((p) => {
                const liveStatus = liveConnectionMap[p.id];
                // Render label based on the cloud's actual live-ness AND user's connection
                let statusLabel;
                let badgeVariant = 'secondary';
                let bgClass = 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700';

                if (p.live === false) {
                  statusLabel = 'Coming Soon';
                  bgClass = 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 opacity-60';
                } else if (liveStatus === true) {
                  statusLabel = 'Connected';
                  badgeVariant = 'default';
                  bgClass = 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50';
                } else {
                  // LIVE cloud not yet connected — invite the user with a friendly label
                  statusLabel = 'Ready to Connect';
                  bgClass = 'bg-sky-50 dark:bg-sky-900/15 border-sky-200 dark:border-sky-800/40 hover:bg-sky-100 dark:hover:bg-sky-900/25 hover:border-sky-300 dark:hover:border-sky-700/60 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-md';
                }

                // Decide if the card should be clickable (live cloud, not yet connected, has a connector)
                const HAS_CONNECTOR = ['aws', 'azure', 'gcp', 'snowflake', 'databricks'];
                const canConnect = p.live !== false && !liveStatus && HAS_CONNECTOR.includes(p.id);
                const handleClick = canConnect
                  ? () => setOpenConnector(p.id)
                  : (p.id === 'digitalocean' && !liveStatus)
                    ? () => window.location.assign('/cloud-audit?provider=digitalocean')
                    : undefined;

                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={handleClick}
                    disabled={!handleClick}
                    aria-label={`${p.name} — ${statusLabel}`}
                    className={`p-3 rounded-lg text-center border ${bgClass} ${handleClick ? '' : 'cursor-default'}`}
                    data-testid={`conn-status-${p.id}`}
                  >
                    <div className="flex justify-center mb-1.5">
                      <p.LogoComponent />
                    </div>
                    <p className="font-medium text-xs">{p.name}</p>
                    <Badge variant={badgeVariant} className="mt-1 text-[11px]">
                      {statusLabel}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cloud Connect Modals — opened when user clicks a "Ready to Connect" card */}
      <AWSConnect
        isOpen={openConnector === 'aws'}
        onClose={() => setOpenConnector(null)}
        onConnected={() => { setOpenConnector(null); fetchAllData(); }}
      />
      <AzureConnect
        isOpen={openConnector === 'azure'}
        onClose={() => setOpenConnector(null)}
        onConnected={() => { setOpenConnector(null); fetchAllData(); }}
      />
      <GCPConnect
        isOpen={openConnector === 'gcp'}
        onClose={() => setOpenConnector(null)}
        onConnected={() => { setOpenConnector(null); fetchAllData(); }}
      />
      <SnowflakeConnect
        isOpen={openConnector === 'snowflake'}
        onClose={() => setOpenConnector(null)}
        onConnected={(creds) => {
          if (creds && setSnowflakeCreds) setSnowflakeCreds(creds);
          setOpenConnector(null);
          toast.success('Snowflake connected!');
        }}
      />
      <DatabricksConnect
        isOpen={openConnector === 'databricks'}
        onClose={() => setOpenConnector(null)}
        onConnected={(creds) => {
          if (creds && setDatabricksCreds) setDatabricksCreds(creds);
          setOpenConnector(null);
          toast.success('Databricks connected!');
        }}
      />
    </div>
  );
};

export default CloudCostDashboard;
